import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { useKTX2 } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import isEqual from "lodash/isEqual";
import { NoColorSpace, SRGBColorSpace } from "three";
import { LinearEncoding, sRGBEncoding } from "@react-three/drei/helpers/deprecated";

// =====================
// DEBUG SWITCH
// =====================
const DEBUG_TEXTURES = false;

function dbgLog(...args) {
  if (!DEBUG_TEXTURES) return;
  // eslint-disable-next-line no-console
  console.debug("[TexturesLoader]", ...args);
}

// =====================
// DEFAULT TEXTURES SET
// =====================
const DEFAULT_PROPS_COLORS = {
  walls_color: { id: 0, hexa: "#ffffff", type: "matt" },
  walls_inside_color: { id: 0, hexa: "#f5f5f5", type: "matt" },
  roof_color: { id: 0, hexa: "#ffffff", type: "matt" },
  gates_color: { id: 0, hexa: "#ffffff", type: "matt" },
  gates_double_leaf_color: { id: 0, hexa: "#ffffff", type: "matt" },
  gates_sectional_color: { id: 0, hexa: "#ffffff", type: "matt" },
  gates_roller_color: { id: 0, hexa: "#ffffff", type: "matt" },
  gates_swing_color: { id: 0, hexa: "#ffffff", type: "matt" },
  tooling_color: { id: 0, hexa: "#ffffff", type: "matt" },
  roof_tooling_color: { id: 0, hexa: "#ffffff", type: "matt" },
  gutters_color: { id: 0, hexa: "#ffffff", type: "matt" },
  metal_default: { id: 0, hexa: "#ffffff", type: "matt" },
  plastic_default: { id: 0, hexa: "#ffffff", type: "matt" },
};

const FILC_URL = "/textures/addons_ktx2/filc-textura.ktx2";

// było:
const TOOLING_TEXTURE = "/textures/metal_ktx2/metal_for_poles.ktx2";

// ✅ metal_default ma mieć tę teksturę
const METAL_DEFAULT_TEXTURE = "/textures/metal_ktx2/metal_for_poles.ktx2";

// ✅ PLASTIC_DEFAULT: masz tylko base + normal
const PLASTIC_DEFAULT_BASE =
  "/textures/plastic_ktx2/Poliigon_PlasticMoldDryBlast_7495_BaseColor.ktx2";
const PLASTIC_DEFAULT_NORMAL =
  "/textures/plastic_ktx2/Poliigon_PlasticMoldDryBlast_7495_Normal.ktx2";

// =====================
// HELPERS
// =====================
function useDeepStable(value, label = "value") {
  const ref = useRef(value);
  const ver = useRef(0);

  if (!isEqual(ref.current, value)) {
    ref.current = value;
    ver.current += 1;
    dbgLog(`useDeepStable changed: ${label}`, { ver: ver.current });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ref.current, [ver.current]);
}

function tuneTexture(tex, { kind = "linear", anisotropy = 8 } = {}) {
  if (!tex) return;

  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;

  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;

  tex.anisotropy = anisotropy;

  if ("colorSpace" in tex) {
    tex.colorSpace = kind === "srgb" ? SRGBColorSpace : NoColorSpace;
  } else if ("encoding" in tex) {
    tex.encoding = kind === "srgb" ? sRGBEncoding : LinearEncoding;
  }

  tex.needsUpdate = true;
}

function buildTexturePlan(colorsReducer, propsColors) {
  const translation = {
    pressing: {
      1: "T-7-vertical",
      2: "T-7-horizontal",
      3: "T-14-vertical",
      4: "T-14-horizontal",
      5: "T-17-vertical",
      6: "T-17-horizontal",
    },
    gate_pressing: {
      1: "T-7-vertical",
      2: "T-7-horizontal",
      3: "T-14-vertical",
      4: "T-14-horizontal",
      5: "T-17-vertical",
      6: "T-17-horizontal",
    },
  };

  const pressingId = colorsReducer?.pressing ?? 1;

  const names = Object.keys(propsColors).sort();
  const urls = [];
  const meta = [];

  const defaultTextureWidth = 6.7; // cm
  const t14DefaultWidth = 3.5; // cm
  const startingError = t14DefaultWidth / defaultTextureWidth;

  for (const name of names) {
    const color = propsColors[name] || { id: 0, hexa: "#ffffff", type: "matt" };
    let pressing = null;

    if (name.includes("walls")) {
      pressing = translation.pressing[pressingId];
    } else if (name.includes("gates")) {
      if (name.includes("double_leaf")) {
        pressing =
          translation.gate_pressing[colorsReducer?.gates_pressing_double_leaf_gate ?? 1];
      } else if (name.includes("swing")) {
        pressing = translation.gate_pressing[colorsReducer?.gates_pressing_swing_gate ?? 1];
      } else if (name.includes("sectional")) {
        pressing =
          translation.gate_pressing[colorsReducer?.gates_pressing_sectional_gate ?? 1];
      } else if (name.includes("roller")) {
        pressing = translation.gate_pressing[colorsReducer?.gates_pressing_roller_gate ?? 1];
      }
    }

    let dir = "vertical";
    let materialType = "galvanized";
    let metalnessValue = 0;
    let roughnessValue = 0;

    if (pressing?.includes("horizontal")) dir = "horizontal";

    const base = `/textures/sheet_metal/ocynk/${dir}_ktx2/corrugated_iron_02`;

    if (color.type === "wooden") {
      urls.push(
        `/textures/wood_ktx2/jasny-orzech-basecolor.ktx2`,
        `${base}_arm_1k.ktx2`,
        `${base}_nor_gl_1k.ktx2`
      );
      metalnessValue = 0;
      roughnessValue = 0.4;
      materialType = "wooden";
    } else if (color.type === "matt") {
      urls.push(`${base}_diff_1k.ktx2`, `${base}_arm_1k.ktx2`, `${base}_nor_gl_1k.ktx2`);
      metalnessValue = 1;
      roughnessValue = 0.4;
      materialType = "matt";
    } else {
      urls.push(`${base}_diff_1k.ktx2`, `${base}_arm_1k.ktx2`, `${base}_nor_gl_1k.ktx2`);
      metalnessValue = 1;
      roughnessValue = 0.2;
      materialType = "glossy";
    }

    let multiplierH = startingError;
    let multiplierV = startingError;

    if (pressing?.includes("T-7")) {
      if (pressing?.includes("vertical")) {
        multiplierH = 2.14 * startingError;
        multiplierV = 1;
      } else {
        multiplierH = 1;
        multiplierV = 2.14 * startingError;
      }
    } else if (pressing?.includes("T-17")) {
      if (pressing?.includes("vertical")) {
        multiplierH = 4.85 * startingError;
        multiplierV = 1;
      } else {
        multiplierH = 1;
        multiplierV = 4.85 * startingError;
      }
    }

    meta.push({
      name,
      hex: color.hexa,
      colorId: color.id,
      pressing: pressing ?? null,
      multiplierH,
      multiplierV,
      metalnessValue,
      roughnessValue,
      materialType,
    });
  }

  return { urls, meta, names };
}

// =====================
// LOADER CHILD
// =====================
function TextureLoaderChild({ colorsReducer, propsColors, onLoaded, basisPath }) {
  const gl = useThree((s) => s.gl);
  const maxAniso = useMemo(() => gl?.capabilities?.getMaxAnisotropy?.() ?? 8, [gl]);

  const { urls, meta, names } = useMemo(
    () => buildTexturePlan(colorsReducer, propsColors),
    [colorsReducer, propsColors]
  );

  // KTX2 (Diff/ARM/Nor)
  const loaded = useKTX2(urls, basisPath);

  // FILC KTX2
  const [filcLoaded] = useKTX2([FILC_URL], basisPath);
  const filcTextureBase = filcLoaded;

  // TOOLING METAL KTX2
  const [toolingLoaded] = useKTX2([TOOLING_TEXTURE], basisPath);
  const toolingTextureBase = toolingLoaded;

  // METAL_DEFAULT KTX2
  const [metalDefaultLoaded] = useKTX2([METAL_DEFAULT_TEXTURE], basisPath);
  const metalDefaultTextureBase = metalDefaultLoaded;

  // ✅ PLASTIC_DEFAULT (base + normal)
  const [plasticBaseLoaded, plasticNormalLoaded] = useKTX2(
    [PLASTIC_DEFAULT_BASE, PLASTIC_DEFAULT_NORMAL],
    basisPath
  );
  const plasticBaseBase = plasticBaseLoaded;
  const plasticNormalBase = plasticNormalLoaded;

  // cache klonów
  const clonesCacheRef = useRef({
    key: null,
    slots: null,
    filc: null,
    tooling: null,
    metalDefault: null,
    plasticBase: null,
    plasticNormal: null,
  });

  const urlsKey = useMemo(() => urls.join("|"), [urls]);

  // cacheKey musi uwzględniać też filc/tooling/metal_default/plastic + ich realny stan
  const cacheKey = useMemo(() => {
    const readyFilc = filcTextureBase ? "1" : "0";
    const readyTool = toolingTextureBase ? "1" : "0";
    const readyMetalDef = metalDefaultTextureBase ? "1" : "0";
    const readyPlBase = plasticBaseBase ? "1" : "0";
    const readyPlNorm = plasticNormalBase ? "1" : "0";

    return `${urlsKey}|${FILC_URL}|${TOOLING_TEXTURE}|${METAL_DEFAULT_TEXTURE}|${PLASTIC_DEFAULT_BASE}|${PLASTIC_DEFAULT_NORMAL}|${readyFilc}|${readyTool}|${readyMetalDef}|${readyPlBase}|${readyPlNorm}`;
  }, [
    urlsKey,
    filcTextureBase,
    toolingTextureBase,
    metalDefaultTextureBase,
    plasticBaseBase,
    plasticNormalBase,
  ]);

  const textures = useMemo(() => {
    if (clonesCacheRef.current.key !== cacheKey) {
      const slots = {};

      for (let i = 0; i < names.length; i++) {
        const i3 = i * 3;

        const baseDiff = loaded[i3 + 0];
        const baseArm = loaded[i3 + 1];
        const baseNor = loaded[i3 + 2];

        const diff = baseDiff?.clone?.() ?? baseDiff;
        const arm = baseArm?.clone?.() ?? baseArm;
        const nor = baseNor?.clone?.() ?? baseNor;

        if (diff) diff.name = `${names[i]}:diff`;
        if (arm) arm.name = `${names[i]}:arm`;
        if (nor) nor.name = `${names[i]}:nor`;

        tuneTexture(diff, { kind: "srgb", anisotropy: maxAniso });
        tuneTexture(arm, { kind: "linear", anisotropy: maxAniso });
        tuneTexture(nor, { kind: "linear", anisotropy: maxAniso });

        slots[names[i]] = { diff, arm, nor };
      }

      // filc
      const filc = filcTextureBase?.clone?.() ?? filcTextureBase ?? null;
      if (filc) {
        filc.name = `roof_color:filc`;
        tuneTexture(filc, { kind: "srgb", anisotropy: maxAniso });
      }

      // tooling
      const tooling = toolingTextureBase?.clone?.() ?? toolingTextureBase ?? null;
      if (tooling) {
        tooling.name = `tooling_color:texture`;
        tuneTexture(tooling, { kind: "srgb", anisotropy: maxAniso });
      }

      // metal_default
      const metalDefault = metalDefaultTextureBase?.clone?.() ?? metalDefaultTextureBase ?? null;
      if (metalDefault) {
        metalDefault.name = `metal_default:texture`;
        tuneTexture(metalDefault, { kind: "srgb", anisotropy: maxAniso });
      }

      // ✅ plastic base (sRGB)
      const plasticBase = plasticBaseBase?.clone?.() ?? plasticBaseBase ?? null;
      if (plasticBase) {
        plasticBase.name = `plastic_default:base`;
        tuneTexture(plasticBase, { kind: "srgb", anisotropy: maxAniso });
      }

      // ✅ plastic normal (linear)
      const plasticNormal = plasticNormalBase?.clone?.() ?? plasticNormalBase ?? null;
      if (plasticNormal) {
        plasticNormal.name = `plastic_default:normal`;
        tuneTexture(plasticNormal, { kind: "linear", anisotropy: maxAniso });
      }

      clonesCacheRef.current = {
        key: cacheKey,
        slots,
        filc,
        tooling,
        metalDefault,
        plasticBase,
        plasticNormal,
      };

      dbgLog("Clones cache rebuilt", { cacheKey, slotsCount: Object.keys(slots).length });
    }

    const out = {};
    const slots = clonesCacheRef.current.slots;
    const filcTex = clonesCacheRef.current.filc;
    const toolingTex = clonesCacheRef.current.tooling;
    const metalDefaultTex = clonesCacheRef.current.metalDefault;
    const plasticBaseTex = clonesCacheRef.current.plasticBase;
    const plasticNormalTex = clonesCacheRef.current.plasticNormal;

    for (let i = 0; i < names.length; i++) {
      const slot = slots[names[i]];

      out[names[i]] = {
        hex: meta[i].hex,
        colorId: meta[i].colorId,
        pressing: meta[i].pressing,
        materialType: meta[i].materialType,
        multiplierHorizontal: meta[i].multiplierH,
        multiplierVertical: meta[i].multiplierV,
        textureDiff: slot?.diff ?? null,
        textureMetalness: slot?.arm ?? null,
        textureNormalness: slot?.nor ?? null,
        metalnessValue: meta[i].metalnessValue,
        roughnessValue: meta[i].roughnessValue,
      };
    }

    if (!out.roof_color) {
      out.roof_color = {
        hex: "#ffffff",
        colorId: 0,
        pressing: null,
        materialType: "matt",
        multiplierHorizontal: 1,
        multiplierVertical: 1,
        textureDiff: null,
        textureMetalness: null,
        textureNormalness: null,
        metalnessValue: 0,
        roughnessValue: 1,
      };
    }
    out.roof_color.texture = filcTex;

    if (!out.tooling_color) {
      out.tooling_color = {
        hex: "#ffffff",
        colorId: 0,
        pressing: null,
        materialType: "matt",
        multiplierHorizontal: 1,
        multiplierVertical: 1,
        textureDiff: null,
        textureMetalness: null,
        textureNormalness: null,
        metalnessValue: 1,
        roughnessValue: 1,
      };
    }
    out.tooling_color.texture = toolingTex;

    // metal_default
    if (!out.metal_default) {
      out.metal_default = {
        hex: "#ffffff",
        colorId: 0,
        pressing: null,
        materialType: "matt",
        multiplierHorizontal: 1,
        multiplierVertical: 1,
        textureDiff: null,
        textureMetalness: null,
        textureNormalness: null,
        metalnessValue: 1,
        roughnessValue: 0.35,
      };
    }
    out.metal_default.texture = metalDefaultTex;
    out.metal_default.textureDiff = metalDefaultTex;

    // ✅ plastic_default: base + normal (bez roughness mapy)
    if (!out.plastic_default) {
      out.plastic_default = {
        hex: "#ffffff",
        colorId: 0,
        pressing: null,
        materialType: "matt",
        multiplierHorizontal: 1,
        multiplierVertical: 1,
        textureDiff: null,
        textureMetalness: null,
        textureNormalness: null,
        metalnessValue: 0,
        roughnessValue: 0.65, // ustawiasz ręcznie roughness
      };
    }

    // base jako "diff"
    out.plastic_default.texture = plasticBaseTex;
    out.plastic_default.textureDiff = plasticBaseTex;

    // normal map
    out.plastic_default.textureNormalness = plasticNormalTex;
    out.plastic_default.textureNormal = plasticNormalTex;
    out.plastic_default.textureNormalMap = plasticNormalTex;

    // aliasy three.js (jakbyś chciał prosto podpinać)
    out.plastic_default.normalMap = plasticNormalTex;

    return out;
  }, [
    cacheKey,
    loaded,
    meta,
    names,
    filcTextureBase,
    toolingTextureBase,
    metalDefaultTextureBase,
    plasticBaseBase,
    plasticNormalBase,
    maxAniso,
  ]);

  const sentKeyRef = useRef(null);

  const key = useMemo(
    () =>
      JSON.stringify({
        urls,
        meta,
        filc: FILC_URL,
        tooling: TOOLING_TEXTURE,
        metalDefault: METAL_DEFAULT_TEXTURE,
        plasticBase: PLASTIC_DEFAULT_BASE,
        plasticNormal: PLASTIC_DEFAULT_NORMAL,
      }),
    [urls, meta]
  );

  useEffect(() => {
    const changed = sentKeyRef.current !== key;
    if (changed) {
      onLoaded(textures);
      sentKeyRef.current = key;
    }
  }, [key, textures, onLoaded]);

  return null;
}

// =====================
// PUBLIC WRAPPER
// =====================
export function TexturesLoader({
                                 colorsReducer,
                                 propsColors,
                                 onLoad,
                                 includeDefaults = true,
                                 defaultPropsColors = DEFAULT_PROPS_COLORS,
                                 basisPath,
                               }) {
  const stableColors = useDeepStable(colorsReducer, "colorsReducer");
  const stablePropsColors = useDeepStable(propsColors || {}, "propsColors");

  const mergedPropsColors = useMemo(() => {
    if (!includeDefaults) return stablePropsColors;
    return { ...defaultPropsColors, ...stablePropsColors };
  }, [includeDefaults, defaultPropsColors, stablePropsColors]);

  const stableMergedPropsColors = useDeepStable(mergedPropsColors, "mergedPropsColors");

  const childKey = useMemo(
    () => JSON.stringify({ c: stableColors, p: stableMergedPropsColors }),
    [stableColors, stableMergedPropsColors]
  );

  return (
    <Suspense fallback={null}>
      <TextureLoaderChild
        key={childKey}
        colorsReducer={stableColors}
        propsColors={stableMergedPropsColors}
        onLoaded={onLoad}
        basisPath={basisPath}
      />
    </Suspense>
  );
}
